import React, { useState } from 'react';
import { FormikProps } from 'formik';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import InfoIcon from '@material-ui/icons/Info';
import VisibilityIcon from '@material-ui/icons/Visibility';
import WarningIcon from '@material-ui/icons/Warning';

import { OutlineButton, PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { DeliveryOption } from '../../../lib/types/communication.types';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { focusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.helpers';
import { IComposePanelFields, ITestSourceContext } from './compose-panel.types';
import { COMPOSE_FORM_ID } from './compose-panel.constants';
import { TestSendDialog } from './test-send-dialog.component';
import { buildUnreachableLabel, buildZeroMessageReason } from './compose-panel-footer.helpers';
import { Footer, RecipientCount, UnreachableWarning } from './compose-panel.styles';

interface IComposePanelFooterProps {
  deliveryOption: DeliveryOption;
  recipientCount: number;
  messageCount: number;
  unreachableCount: number;
  recipientCountLoading: boolean;
  isSubmitting: boolean;
  validateForm: FormikProps<IComposePanelFields>['validateForm'];
  setTouched: FormikProps<IComposePanelFields>['setTouched'];
  // Opens the preview modal — a read-only action, so it doesn't gate on validation.
  onPreview: () => void;
  testSource: ITestSourceContext;
  onSendTest: (emails: string[], phones: string[], testSourceId?: string) => Promise<number>;
  testRecipientsStorageKey: string;
}

export const ComposePanelFooter: React.FC<IComposePanelFooterProps> = ({
  deliveryOption,
  recipientCount,
  messageCount,
  unreachableCount,
  recipientCountLoading,
  isSubmitting,
  validateForm,
  setTouched,
  onPreview,
  testSource,
  onSendTest,
  testRecipientsStorageKey
}) => {
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  // A test mirrors a real send, so it gates on the same validation: on failure
  // surface the errors AND focus the first invalid field before opening.
  const handleOpenTestDialog = async () => {
    const errors = await validateForm();
    const keys = Object.keys(errors);
    if (keys.length > 0) {
      setTouched(keys.reduce((acc, k) => ({ ...acc, [k]: true }), {}), false);
      const formEl = document.getElementById(COMPOSE_FORM_ID);
      if (formEl) focusFirstInvalidField(formEl, keys);
      return;
    }
    setTestDialogOpen(true);
  };

  // Not memoized: cheap string builds over primitives, not fed to any hook dep.
  const unreachableLabel = buildUnreachableLabel(deliveryOption, unreachableCount);
  // Footer note for the no-message case so the user can fix delivery/audience
  // before Send; the button stays enabled (Send with 0 → post-submit error).
  const showZeroMessageReason = !recipientCountLoading && !isSubmitting && messageCount === 0;
  const zeroMessageReason = showZeroMessageReason
    ? buildZeroMessageReason(deliveryOption, unreachableCount)
    : '';

  return (
    <>
      <Footer>
        <Box flexGrow={1}>
          <Typography variant="body2" color="textSecondary">
            <RecipientCount>{recipientCountLoading ? '…' : recipientCount}</RecipientCount>{' '}
            {pluralize(recipientCount, 'recipient')}
            {' · '}
            {recipientCountLoading ? '…' : messageCount} {pluralize(messageCount, 'message')}
            {/* Info icon only when total messages exceeds unique recipients — a
                recipient is on file for multiple players. */}
            {!recipientCountLoading && messageCount > recipientCount && (
              <Tooltip title="Some recipients will receive a message for each of multiple players they're associated with." arrow>
                <InfoIcon style={{ fontSize: 14, color: '#ccc', cursor: 'help', marginLeft: 4, verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </Typography>
          {unreachableCount > 0 && unreachableLabel && (
            <UnreachableWarning>
              <WarningIcon /> {unreachableLabel}
            </UnreachableWarning>
          )}
          {zeroMessageReason && (
            <UnreachableWarning>
              <WarningIcon /> {zeroMessageReason}
            </UnreachableWarning>
          )}
        </Box>
        {/* Preview — read-only tertiary action; a text button, distinct from
            Send test (outlined) and Send (contained). Opens the preview modal. */}
        <SecondaryButton
          type="button"
          onClick={onPreview}
          disabled={isSubmitting}
          startIcon={<VisibilityIcon fontSize="small" />}
        >
          Preview
        </SecondaryButton>
        {/* Send test — secondary action; validates the same fields the real send
            does first. Submit button standard: REACT_CONVENTIONS.md "Submit buttons". */}
        <OutlineButton type="button" onClick={handleOpenTestDialog} disabled={isSubmitting}>
          Send test
        </OutlineButton>
        <PrimaryButton type="submit" disabled={isSubmitting}>
          {isSubmitting && <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />}
          {isSubmitting
            ? 'Sending…'
            : `Send ${recipientCountLoading ? '…' : messageCount} ${pluralize(messageCount, 'message')}`}
        </PrimaryButton>
      </Footer>

      <TestSendDialog
        open={testDialogOpen}
        deliveryOption={deliveryOption}
        storageKey={testRecipientsStorageKey}
        testSource={testSource}
        onClose={() => setTestDialogOpen(false)}
        onSendTest={onSendTest}
      />
    </>
  );
};
